import { useEffect, useMemo, useRef, useState } from 'react';
import { Composer } from './components/Composer';
import { Conversation } from './components/Conversation';
import { Header } from './components/Header';
import { RunInspector } from './components/RunInspector';
import { fetchAgents, fetchThreadMessages, streamAgentRun } from './lib/mastra-api';
import { createRun, finishRunAfterStream, finishRunWithError, reduceRunEvent } from './lib/run-state';
import {
  clearSnapshot,
  loadAgentId,
  loadSnapshot,
  loadThreadId,
  saveAgentId,
  saveSnapshot,
  saveThreadId,
} from './lib/storage';
import type { AgentSummary, AssistantMessage, ChatMessage, UserMessage } from './lib/types';

const API_BASE = (new URLSearchParams(window.location.search).get('api') ?? 'https://mastra.logospowered.com').replace(/\/$/, '');
const RESOURCE_ID = 'web';
const FALLBACK_AGENTS: AgentSummary[] = [
  { id: 'lead', name: 'Lead', description: 'Supervisor network', tools: [] },
  { id: 'scout', name: 'Scout', description: 'Exploration and current-time questions', tools: ['timezoneClockTool'] },
  { id: 'analyst', name: 'Analyst', description: 'Numbers and probability', tools: ['diceRollerTool'] },
  { id: 'scribe', name: 'Scribe', description: 'Writing and editing', tools: ['wordCounterTool'] },
];

export default function App() {
  const [threadId, setThreadId] = useState(loadThreadId);
  const [agentId, setAgentId] = useState(loadAgentId);
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadSnapshot(threadId));
  const [agents, setAgents] = useState<AgentSummary[]>(FALLBACK_AGENTS);
  const [connection, setConnection] = useState<'checking' | 'online' | 'offline'>('checking');
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [selectedRunId, setSelectedRunId] = useState<string | undefined>(() => {
    const latest = [...loadSnapshot(threadId)].reverse().find((message) => message.role === 'assistant');
    return latest?.role === 'assistant' ? latest.run.id : undefined;
  });
  const [mobileInspectorOpen, setMobileInspectorOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setConnection('checking');
    fetchAgents(API_BASE, controller.signal)
      .then((registry) => {
        if (registry.length) setAgents(registry);
        setConnection('online');
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setConnection('offline');
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (messages.length > 0) return;
    const controller = new AbortController();
    fetchThreadMessages(API_BASE, threadId, agentId, controller.signal)
      .then((history) => {
        if (!history.length) return;
        setMessages(history);
        const latest = [...history].reverse().find((message) => message.role === 'assistant');
        if (latest?.role === 'assistant') setSelectedRunId(latest.run.id);
        setNotice(`${history.length} messages restored from Mastra memory`);
      })
      .catch(() => {
        // A stale or empty thread is a valid starting state.
      });
    return () => controller.abort();
  }, [agentId, messages.length, threadId]);

  useEffect(() => {
    const timer = window.setTimeout(() => saveSnapshot(threadId, messages), 450);
    return () => window.clearTimeout(timer);
  }, [messages, threadId]);

  const selectedRun = useMemo(() => {
    const message = messages.find((item) => item.role === 'assistant' && item.run.id === selectedRunId);
    return message?.role === 'assistant' ? message.run : undefined;
  }, [messages, selectedRunId]);

  const selectedAgent = agents.find((agent) => agent.id === agentId) ?? FALLBACK_AGENTS[0];

  const updateRun = (messageId: string, update: (run: AssistantMessage['run']) => AssistantMessage['run']) => {
    setMessages((current) => current.map((message) => message.id === messageId && message.role === 'assistant'
      ? { ...message, run: update(message.run) }
      : message));
  };

  const send = async (override?: string) => {
    const text = (override ?? input).trim();
    if (!text || busy) return;

    setInput('');
    setNotice('');
    setBusy(true);
    saveThreadId(threadId);

    const now = Date.now();
    const run = createRun({
      agentId,
      mode: agentId === 'lead' ? 'network' : 'stream',
      prompt: text,
      threadId,
      startedAt: now,
    });
    const userMessage: UserMessage = { id: crypto.randomUUID(), role: 'user', text, createdAt: now };
    const assistantMessage: AssistantMessage = { id: crypto.randomUUID(), role: 'assistant', run, createdAt: now };
    setMessages((current) => [...current, userMessage, assistantMessage]);
    setSelectedRunId(run.id);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamAgentRun({
        apiBase: API_BASE,
        agentId,
        message: text,
        threadId,
        resourceId: RESOURCE_ID,
        signal: controller.signal,
        onEvent: (event) => updateRun(assistantMessage.id, (current) => reduceRunEvent(current, event)),
      });
      updateRun(assistantMessage.id, finishRunAfterStream);
      setConnection('online');
    } catch (error) {
      const aborted = controller.signal.aborted || (error instanceof DOMException && error.name === 'AbortError');
      updateRun(assistantMessage.id, (current) => finishRunWithError(current, error, aborted ? 'aborted' : 'error'));
      if (!aborted) {
        setConnection('offline');
        setNotice(error instanceof Error ? error.message : 'The run failed');
      }
    } finally {
      abortRef.current = null;
      setBusy(false);
    }
  };

  const startNewThread = () => {
    if (busy) return;
    const next = crypto.randomUUID();
    setThreadId(next);
    saveThreadId(next);
    clearSnapshot();
    setMessages([]);
    setSelectedRunId(undefined);
    setNotice('New thread ready');
  };

  return (
    <div className="app-shell">
      <Header
        apiBase={API_BASE}
        agents={agents}
        agentId={agentId}
        connection={connection}
        threadId={threadId}
        busy={busy}
        onAgentChange={(next) => { setAgentId(next); saveAgentId(next); }}
        onNewThread={startNewThread}
        onOpenInspector={() => setMobileInspectorOpen(true)}
      />
      {notice && <div className={`notice-bar${connection === 'offline' ? ' error' : ''}`}>{notice}</div>}
      <div className="workspace">
        <section className="chat-pane" aria-label="Conversation">
          <Conversation
            messages={messages}
            selectedRunId={selectedRunId}
            agentName={selectedAgent.name}
            onSelectRun={(id) => { setSelectedRunId(id); setMobileInspectorOpen(true); }}
            onExample={(prompt) => void send(prompt)}
          />
          <Composer
            value={input}
            busy={busy}
            agentName={selectedAgent.name}
            onChange={setInput}
            onSend={() => void send()}
            onStop={() => abortRef.current?.abort()}
          />
        </section>
        <RunInspector run={selectedRun} mobileOpen={mobileInspectorOpen} onClose={() => setMobileInspectorOpen(false)} />
      </div>
    </div>
  );
}
